/**
 * What runs here (YAG-849), under the environment checklist: the record the
 * last setup run, build or bench left of this repository. Tools with their
 * version and whether the sandbox carries them or the build installed them,
 * how the dependency installs went, each check's last result and time, the
 * registry credentials and whether their variable is set, and what is missing
 * with the sentence that fixes it.
 *
 * Amber is the product telling on itself: a missing entry, a failed install
 * and a red check wear it. Nothing here is red. The wording is worked out in
 * `environmentPage.ts` and `missingSentence`; this file is markup.
 */
import { missingSentence, type RepoCapabilityRecord } from "@yagni/shared";
import { Chip } from "../ui/index.js";
import {
  capabilityAsOf,
  capabilityCheckWhen,
  checkStatusWord,
  dependencyStatusWord,
  evidenceLine,
  missingVariable,
  toolOriginWord,
} from "./environmentPage.js";

export const NOTHING_RAN_YET =
  "Nothing has run here yet. The first setup run or build records what runs.";

const LIST = "wrk-preflight-list";

/** The one-character mark in front of a row, in the machine-fact voice. */
function Mark({ ok, amber }: { ok: boolean; amber: boolean }) {
  return (
    <span className="wrk-preflight-mark" aria-hidden="true">
      {ok ? "✓" : amber ? "!" : "–"}
    </span>
  );
}

export function WhatRunsHere({
  record,
  onSetVariable,
  now,
}: {
  record: RepoCapabilityRecord | null;
  /** Opens the Variables row on this name, to choose where its value is set. */
  onSetVariable: (name: string) => void;
  now?: Date;
}) {
  if (!record) {
    return (
      <div className="blk" data-testid="what-runs-here">
        <div className="bh">
          <span className="label">What runs here</span>
        </div>
        <p className="qa-hint" data-testid="what-runs-here-empty">
          {NOTHING_RAN_YET}
        </p>
      </div>
    );
  }

  const toolchainNames = new Set(record.toolchain.map((tool) => tool.name));
  const alsoOnPath = record.toolsOnPath.filter((tool) => !toolchainNames.has(tool.name));
  const evidence = evidenceLine(record.evidence);

  return (
    <div className="blk" data-testid="what-runs-here">
      <div className="bh">
        <span className="label">What runs here</span>
        <span className="fact" data-testid="what-runs-here-as-of">
          {capabilityAsOf(record, now)}
        </span>
      </div>
      {record.headSha && <p className="qa-fact">at {record.headSha.slice(0, 7)}</p>}

      <dl className="qa-summary">
        {(record.toolchain.length > 0 || alsoOnPath.length > 0) && (
          <div>
            <dt>Tools</dt>
            <dd>
              <ul className={LIST} aria-label="Tools" data-testid="what-runs-here-tools">
                {record.toolchain.map((tool) => (
                  <li key={`toolchain:${tool.name}`} className="is-ok">
                    <Mark ok amber={false} />
                    <span className="wrk-preflight-text">
                      <code>
                        {tool.name} {tool.version}
                      </code>
                      <span className="qa-hint">{toolOriginWord(tool.origin)}</span>
                    </span>
                  </li>
                ))}
                {alsoOnPath.map((tool) => (
                  <li key={`path:${tool.name}`} className="is-ok">
                    <Mark ok amber={false} />
                    <span className="wrk-preflight-text">
                      <code>
                        {tool.name}
                        {tool.version ? ` ${tool.version}` : ""}
                      </code>
                      <span className="qa-hint">on PATH</span>
                    </span>
                  </li>
                ))}
              </ul>
            </dd>
          </div>
        )}
        {record.dependencies.length > 0 && (
          <div>
            <dt>Dependencies</dt>
            <dd>
              <ul
                className={LIST}
                aria-label="Dependencies"
                data-testid="what-runs-here-dependencies"
              >
                {record.dependencies.map((dependency, position) => (
                  <li
                    key={`${position}:${dependency.command}`}
                    className={
                      dependency.status === "installed"
                        ? "is-ok"
                        : dependency.status === "failed"
                          ? "is-missing"
                          : undefined
                    }
                  >
                    <Mark
                      ok={dependency.status === "installed"}
                      amber={dependency.status === "failed"}
                    />
                    <span className="wrk-preflight-text">
                      <code>{dependency.command}</code>
                      <span>{dependencyStatusWord(dependency.status)}</span>
                      {dependency.cause && <span className="qa-fact">{dependency.cause}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </dd>
          </div>
        )}
        {record.checks.length > 0 && (
          <div>
            <dt>Checks</dt>
            <dd>
              <ul className={LIST} aria-label="Checks" data-testid="what-runs-here-checks">
                {record.checks.map((check, position) => (
                  <li
                    key={`${position}:${check.command}`}
                    className={
                      check.status === "green"
                        ? "is-ok"
                        : check.status === "red"
                          ? "is-missing"
                          : undefined
                    }
                    data-status={check.status}
                  >
                    <Mark ok={check.status === "green"} amber={check.status === "red"} />
                    <span className="wrk-preflight-text">
                      <code>{check.command}</code>
                      <span>{checkStatusWord(check.status)}</span>
                      <span className="qa-fact">{capabilityCheckWhen(check.at)}</span>
                      {check.cause && <span className="qa-fact">{check.cause}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </dd>
          </div>
        )}
        {record.credentials.length > 0 && (
          <div>
            <dt>Credentials</dt>
            <dd>
              <ul
                className={LIST}
                aria-label="Credentials"
                data-testid="what-runs-here-credentials"
              >
                {record.credentials.map((credential) => (
                  <li
                    key={`${credential.host}:${credential.variable}`}
                    data-testid={`what-runs-here-credential-${credential.variable}`}
                  >
                    <span className="wrk-preflight-text">
                      <code>{credential.host}</code>
                      <code>{credential.variable}</code>
                      <Chip tone={credential.status === "set" ? "success" : "honesty"}>
                        {credential.status === "set" ? "Set" : "Missing"}
                      </Chip>
                    </span>
                  </li>
                ))}
              </ul>
            </dd>
          </div>
        )}
      </dl>

      {evidence && (
        <p className="qa-hint" data-testid="what-runs-here-evidence">
          {evidence}
        </p>
      )}

      {record.missing.length > 0 && (
        <div className="qa-notice flex flex-col gap-[6px]" data-testid="what-runs-here-missing">
          <span className="label text-[var(--honesty)]">Missing</span>
          <ul className="m-0 flex list-none flex-col gap-[6px] p-0" aria-label="Missing">
            {record.missing.map((entry, position) => {
              const variable = missingVariable(entry);
              return (
                <li
                  key={`${position}:${entry.kind}:${entry.name}`}
                  className="flex flex-wrap items-baseline gap-x-[10px]"
                >
                  <span>{missingSentence(entry)}</span>
                  {variable && (
                    <button
                      type="button"
                      className="wrk-quiet-btn"
                      aria-label={`Set ${variable}`}
                      onClick={() => onSetVariable(variable)}
                    >
                      Set
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
